import type { User, Contributor } from "@/types";
import * as Sentry from "@sentry/react";

export function getInitials(user: User | null | undefined) {
  if (user?.first_name) {
    return (user.first_name.charAt(0) + user.last_name.charAt(0)).toUpperCase();
  } else if (user?.username) {
    return user.username.charAt(0).toUpperCase();
  } else if (user?.email) {
    return user.email.charAt(0).toUpperCase();
  }
  return "?";
}

export function getAllUserLabels(user: User) {
  return user.organizations.flatMap((organization) => organization.labels);
}

/**
 * Sorts contributors so main artists appear first, then featured artists.
 * Within each role group, contributors are sorted alphabetically by display name.
 */
export function sortContributorsByRole(contributors: Contributor[]): Contributor[] {
  return [...contributors].sort((a, b) => {
    // Main artists come first
    if (a.role === "main_artist" && b.role !== "main_artist") return -1;
    if (a.role !== "main_artist" && b.role === "main_artist") return 1;
    // Within same role, sort alphabetically by display_name
    return a.display_name.localeCompare(b.display_name);
  });
}

/**
 * Formats an array of contributors into a comma-separated string of display names,
 * with main artists listed first.
 */
export function formatContributorNames(contributors: Contributor[] | undefined): string {
  if (!contributors || contributors.length === 0) return "";
  return sortContributorsByRole(contributors)
    .map((c) => c.display_name)
    .join(", ");
}

/**
 * Fixes radix ui pointer event none issue when escape key is pressed
 */
export const handleCloseAutoFocus = () => {
  document.body.style.pointerEvents = "";
};

export const formatRecordingContributorsForSync = (
  artists: string[],
  contributors: Contributor[],
) => {
  return artists.map((artistId) => ({
    contributor: artistId,
    role: contributors.find((c) => c.id === artistId)?.role || "featured_artist",
  }));
};

/**
 * Converts a ContractPeriod filter value to API date filter parameters
 * Uses the backend's filter parameter names: start_date_after, start_date_before, end_date_after, end_date_before
 */
export function getContractPeriodDates(period: string): {
  start_date_after?: string;
  start_date_before?: string;
  end_date_after?: string;
  end_date_before?: string;
  end_date_active?: boolean;
} {
  const today = new Date();
  today.setUTCHours(0, 0, 0, 0);
  const todayStr = today.toISOString().split("T")[0];

  switch (period) {
    case "all":
      return {};

    case "active_now": {
      // Contracts where start_date <= today AND end_date_active is true
      return {
        start_date_before: todayStr,
        end_date_active: true,
      };
    }

    case "upcoming": {
      // Contracts where start_date > today
      return {
        start_date_after: todayStr,
      };
    }

    case "expired": {
      // Contracts where end_date < today
      return {
        end_date_before: todayStr,
      };
    }

    case "expiring_this_month": {
      // Contracts where end_date is between today and end of this month
      const endOfMonth = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() + 1, 0));
      const endOfMonthStr = endOfMonth.toISOString().split("T")[0];
      return {
        end_date_after: todayStr,
        end_date_before: endOfMonthStr,
      };
    }

    case "expiring_3_months": {
      // Contracts where end_date is between today and 3 months from now
      const threeMonthsFromNow = new Date(today);
      threeMonthsFromNow.setUTCMonth(threeMonthsFromNow.getUTCMonth() + 3);
      const threeMonthsStr = threeMonthsFromNow.toISOString().split("T")[0];
      return {
        end_date_after: todayStr,
        end_date_before: threeMonthsStr,
      };
    }

    default:
      return {};
  }
}

/**
 * Extracts and formats the file extension from a filename
 * Returns the extension in uppercase (e.g., "PDF", "DOC", "PNG")
 * Returns "FILE" if no extension is found
 */
export function getFileExtension(filename: string): string {
  const parts = filename.split(".");
  if (parts.length === 1 || (parts[0] === "" && parts.length === 2)) {
    // No extension found (e.g., "file" or ".hiddenfile")
    return "FILE";
  }
  const extension = parts.pop();
  return extension ? extension.toUpperCase() : "FILE";
}

export const formatFileSize = (bytes: number) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / k ** i).toFixed(2))} ${sizes[i]}`;
};

export const isImageFile = (file: File) => {
  return file.type.startsWith("image/");
};

/**
 * Determines if a file extension corresponds to an image file type
 * @param extension - The file extension (with or without leading dot, case-insensitive)
 * @returns true if the extension is a supported type - jpg, jpeg, png
 */
export const isImageExtension = (extension: string): boolean => {
  const imageExtensions = ["jpg", "jpeg", "png"];

  // Remove leading dot if present and convert to lowercase
  const normalizedExt = extension.replace(/^\./, "").toLowerCase();
  return imageExtensions.includes(normalizedExt);
};

/**
 * Extracts the filename from an S3 presigned URL
 * Removes query parameters and returns just the filename
 * @param url - The full S3 presigned URL
 * @returns The filename extracted from the URL path
 */
export function extractFilenameFromUrl(url: string): string {
  try {
    // Remove query parameters by splitting at '?'
    const pathWithoutQuery = url.split("?")[0];
    // Get the last segment of the path
    const segments = pathWithoutQuery.split("/");
    const filename = segments[segments.length - 1];
    // Decode any URL-encoded characters
    return decodeURIComponent(filename);
  } catch (error) {
    Sentry.captureException(error);
    return "";
  }
}

/**
 * Formats a numeric amount as currency using Intl.NumberFormat
 * @param amount - The numeric amount to format
 * @param currency - The currency code (e.g., "USD", "EUR"). Defaults to "USD"
 * @param locale - The locale to format for (from useLocale())
 */
export function formatCurrency(amount: number, currency: string, locale: string): string {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currency || "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * Formats a string amount as currency, parsing it to a number first
 * Returns the original string if parsing fails
 * @param amount - The string amount to format
 * @param currency - The currency code (e.g., "USD", "EUR"). Defaults to "USD"
 * @param locale - The locale to format for (from useLocale())
 */
export function formatCurrencyFromString(amount: string, currency: string, locale: string): string {
  const numAmount = parseFloat(amount);
  if (Number.isNaN(numAmount)) return amount;
  return formatCurrency(numAmount, currency, locale);
}

/**
 * Formats a numeric amount with grouping and two decimal places
 * @param amount - The amount to format (number or numeric string)
 * @param locale - The locale to format for (from useLocale())
 */
export function formatNumber(amount: number | string, locale: string): string {
  const numAmount = typeof amount === "number" ? amount : parseFloat(amount);
  if (Number.isNaN(numAmount)) return String(amount);
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numAmount);
}

/**
 * Format an FX rate at a fixed 5 fraction digits, e.g. "0,71815".
 * Fixed rather than trimmed so a column of rates lines up and a rate that
 * happens to end in a zero still reads as a rate.
 */
export function formatExchangeRate(rate: number, locale: string): string {
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 5,
    maximumFractionDigits: 5,
  }).format(rate);
}

/** Format an FX rate: up to 6 fraction digits, no trailing-zero padding. */
export function formatRate(rate: number, locale: string): string {
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 6 }).format(rate);
}

export function formatAmountWithCode(amount: number, currency: string, locale: string): string {
  return `${formatNumber(amount, locale)} ${currency}`;
}

export function formatAmountWithCodeFromString(
  amount: string,
  currency: string,
  locale: string,
): string {
  const numAmount = parseFloat(amount);
  if (Number.isNaN(numAmount)) return amount;
  return formatAmountWithCode(numAmount, currency, locale);
}

export function toNumber(value: string | null | undefined): number {
  if (value == null) return 0;
  const n = parseFloat(value);
  return Number.isNaN(n) ? 0 : n;
}
